import difference from 'lodash/difference'

import { Scope } from '../enums/scope'
import { getGlobalBillyIsNewScopesVersion, getGlobalBillyScopes } from './globalBilly'

/**
 * This function supports two use-cases.
 * 1. If requiredScopes is of type Scope or Scope[]:
 *   All listed scopes will be required.
 * 2. If requiredScopes is of type Scope[][]:
 *   At least one of the nested arrays must have all scopes granted to the user.
 *   Example:
 *   [
 *     [Scope.UserRead, Scope.UserWrite],
 *     [Scope.UserWriteAccountant],
 *   ]
 *   The above will return true (authorized) if the granted scopes include either:
 *   Scope.UserRead AND Scope.UserWrite
 *   OR
 *   Scope.UserWriteAccountant
 *
 * @global IMPORTANT NOTE - when using this function, please do not memoize it, as it checks the global window object which might change
 */
export const isAuthorized = (
  requiredScopes: Scope | Scope[] | Scope[][],
  shouldCheckLegacyScopes = false,
  isLegacyPermissionsAuthorized = true,
): boolean => {
  // TODO: Remove legacy if statement when all users got migrated to the new scope version
  if (!getGlobalBillyIsNewScopesVersion() && !shouldCheckLegacyScopes) {
    return isLegacyPermissionsAuthorized
  }

  // Ignore nullable or empty array scopes check
  if (!requiredScopes || !requiredScopes.length) {
    return true
  }

  const requiredScopesArray = Array.isArray(requiredScopes) ? requiredScopes : [requiredScopes]
  const userScopes = getGlobalBillyScopes() || []

  if (requiredScopesArray.every((scopes): scopes is Scope[] => Array.isArray(scopes))) {
    // With nested arrays only one of the nested arrays requires all scopes to be granted
    return requiredScopesArray.some((scopes) => !difference(scopes, userScopes).length)
  }

  // Check that there's no scope which is not authorized
  return !difference(requiredScopesArray, userScopes).length
}
